import React, { useEffect, useState } from 'react';
import {
  Platform, Pressable, ScrollView, StatusBar, View, useWindowDimensions,
} from 'react-native';
import {
  ADMIN_NAV, EXPENSES_NAV, MORE_NAV, NAV, STAFF_NAV, TITLE_KEYS,
  cartTotals, isCloudConfigured, splitPhoneTabs, useAppStore,
} from '@restorant/core';
import type { StringKey, ViewId } from '@restorant/core';
import { MOBILE_BREAKPOINT, useColors, useThemeMode } from './theme';
import { FontsProvider } from './fonts';
import { useT } from './i18n';
import { useSettings } from './menu';
import { useInsets } from './insets';
import { onNetworkChange } from '@restorant/core';
import { PromptProvider } from './prompt';
import { Icon } from './icons/Icon';
import type { IconName } from './icons/Icon';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { ThemeToggle } from './components/ThemeToggle';
import { AccountRow } from './components/AccountRow';
import { SignInScreen } from './components/SignInScreen';
import { SyncBadge } from './components/SyncBadge';
import { TextSizePicker } from './components/TextSizePicker';
import { SettingsButton } from './components/SettingsSheet';
import { NavItem } from './components/NavItem';
import { OrderDetailModal } from './components/OrderDetailModal';
import { SearchBar } from './components/SearchBar';
import { T } from './components/Text';
import { Toast } from './components/Toast';
import { DashboardScreen } from './screens/DashboardScreen';
import { MenuScreen } from './screens/MenuScreen';
import { OrderScreen } from './screens/OrderScreen';
import { OrdersScreen } from './screens/OrdersScreen';
import { CustomersScreen } from './screens/CustomersScreen';
import { OffersScreen } from './screens/OffersScreen';
import { AnalyticsScreen } from './screens/AnalyticsScreen';
import { ExpensesScreen } from './screens/ExpensesScreen';
import { MoreScreen } from './screens/MoreScreen';
import type { MoreEntry } from './screens/MoreScreen';
import { AdminScreen } from './screens/AdminScreen';

/**
 * The tabs this user actually gets.
 *
 * Admin is web-only and owner-only: on a phone it would sit on the counter for
 * a worker to tap, and there is nothing there they are allowed to do.
 *
 * Expenses is owner-only but NOT web-only, which is the one place these two
 * rules part company. Adding staff is an office job done at a desk; paying the
 * vegetable seller happens standing in the market, so it has to be on the
 * owner's own phone. What keeps it off the counter tablet is the role, not the
 * platform — and what keeps the figures safe is the RLS policy behind them,
 * since a hidden tab protects nothing on its own.
 */
/** One entry in either navigation. Widened from the literal NAV tuples. */
interface NavEntry {
  id: string;
  labelKey: string;
  icon: string;
}

function useNavItems(): NavEntry[] {
  const role = useAppStore((s) => s.role);
  const isPlatformAdmin = useAppStore((s) => s.isPlatformAdmin);
  return React.useMemo<NavEntry[]>(() => {
    // The administrator runs the software, not a restaurant: they are on no
    // roster, so every till tab would query a shop they are not a member of
    // and come back empty. They get their own screen and nothing else.
    if (isPlatformAdmin) return Platform.OS === 'web' ? [ADMIN_NAV] : [];
    if (role !== 'owner') return [...NAV];
    return Platform.OS === 'web'
      ? [...NAV, EXPENSES_NAV, STAFF_NAV]
      : [...NAV, EXPENSES_NAV];
  }, [role, isPlatformAdmin]);
}

/**
 * The phone's bottom bar, and what "More" holds.
 *
 * Splitting here rather than inside MoreScreen keeps one answer to "which tabs
 * does this person have": `useNavItems` decides that once, from the role, and
 * both halves below are derived from the same list. A tab can therefore never
 * be missing from the bar AND from More at the same time.
 */
function usePhoneNav(navItems: NavEntry[]) {
  const { width } = useWindowDimensions();
  return React.useMemo(() => {
    const { bar, more } = splitPhoneTabs(navItems, width);
    return {
      // More earns its slot only when it would hold something. On a tablet
      // wide enough for every tab, the bar is just the tabs.
      bar: more.length ? [...bar, MORE_NAV as NavEntry] : bar,
      more: more.map((n) => ({
        id: n.id,
        labelKey: n.labelKey,
        icon: n.icon,
      })) as MoreEntry[],
    };
  }, [navItems, width]);
}

/** Units sitting in every open table combined — the badge on the Order tab. */
function useOpenItemCount(): number {
  return useAppStore((s) =>
    Object.values(s.tables).reduce((n, tbl) => n + cartTotals(tbl.cart, 0).qty, 0),
  );
}

function Sidebar() {
  const colors = useColors();
  const t = useT();
  const settings = useSettings();
  const view = useAppStore((s) => s.view);
  const setView = useAppStore((s) => s.setView);
  const openItems = useOpenItemCount();
  const navItems = useNavItems();

  return (
    <View
      style={{
        width: 234,
        backgroundColor: colors.sidebarTop,
        borderRightWidth: 1,
        borderRightColor: colors.line,
        paddingVertical: 22,
        paddingHorizontal: 16,
        gap: 26,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11, paddingHorizontal: 6 }}>
        <View
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: colors.goldDim,
            backgroundColor: colors.surface,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="store" size={18} color={colors.gold} />
        </View>
        <View style={{ gap: 1, flexShrink: 1 }}>
          <T size={17} weight={600} serif color={colors.gold} numberOfLines={1}>
            {settings.brandName}
          </T>
          <T
            size={9}
            color={colors.textFaint}
            numberOfLines={1}
            style={{ letterSpacing: 0.6 }}
          >
            {settings.brandTagline}
          </T>
        </View>
      </View>

      {/* The nav scrolls and the block below stays pinned. With eight tabs, and
          Devanagari or Gujarati labels being taller than Latin ones, a fixed
          column pushed the language picker and the sign-out button off the
          bottom of a 900px laptop screen entirely. */}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ gap: 4 }}
        showsVerticalScrollIndicator={false}
      >
        {navItems.map((n) => (
          <NavItem
            key={n.id}
            icon={n.icon as IconName}
            label={t(n.labelKey as StringKey)}
            active={view === n.id}
            vertical={false}
            badge={n.id === 'order' ? openItems : undefined}
            testID={`nav-${n.id}`}
            onPress={() => setView(n.id as ViewId)}
          />
        ))}
      </ScrollView>

      <View style={{ paddingHorizontal: 6, gap: 10 }}>
        <AccountRow />
        <SyncBadge />
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          <LanguageSwitcher />
          <ThemeToggle />
          <TextSizePicker />
        </View>
      </View>
    </View>
  );
}

function BottomNav({ bottomInset }: { bottomInset: number }) {
  const colors = useColors();
  const t = useT();
  const view = useAppStore((s) => s.view);
  const setView = useAppStore((s) => s.setView);
  const openItems = useOpenItemCount();
  const navItems = useNavItems();
  const { bar, more } = usePhoneNav(navItems);

  // Standing on one of the tabs that lives inside More keeps More lit, so the
  // bar always says where you are. Without this, opening Analytics from the
  // list would leave no tab highlighted at all.
  const inMore = more.some((m) => m.id === view);

  return (
    <View
      // No longer a horizontal ScrollView. Eight tabs used to scroll sideways,
      // which put half of them off-screen with nothing to suggest they were
      // there. Four fit across a 390px phone, in Devanagari and Gujarati too.
      style={{
        flexDirection: 'row',
        paddingTop: 8,
        paddingBottom: 8 + bottomInset,
        paddingHorizontal: 4,
        backgroundColor: colors.sidebarTop,
        borderTopWidth: 1,
        borderTopColor: colors.line,
      }}
    >
      {bar.map((n) => (
        <View key={n.id} style={{ flex: 1, minWidth: 0 }}>
          <NavItem
            icon={n.icon as IconName}
            label={t(n.labelKey as StringKey)}
            active={n.id === 'more' ? view === 'more' || inMore : view === n.id}
            vertical
            badge={n.id === 'order' ? openItems : undefined}
            testID={`nav-${n.id}`}
            onPress={() => setView(n.id as ViewId)}
          />
        </View>
      ))}
    </View>
  );
}

function TopBar({ narrow }: { narrow: boolean }) {
  const colors = useColors();
  const t = useT();
  const view = useAppStore((s) => s.view);
  const [titleKey, tagKey] = TITLE_KEYS[view] ?? ['title_dashboard', 'tag_dashboard'];

  // The order flow and the history list have their own navigation; a search box
  // that jumps to the Menu tab would fight it. Expenses is the same case — it
  // is not a view of the menu at all, and a box reading "Search a menu item"
  // above a list of rent payments is an invitation to lose the screen.
  const showSearch =
    view !== 'order' && view !== 'orders' && view !== 'customers' &&
    view !== 'expenses' && view !== 'more';

  return (
    <View
      style={{
        flexDirection: narrow ? 'column' : 'row',
        alignItems: narrow ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: narrow ? 12 : 16,
        paddingVertical: narrow ? 14 : 17,
        paddingHorizontal: narrow ? 16 : 26,
        borderBottomWidth: 1,
        borderBottomColor: colors.line,
        backgroundColor: colors.bg,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        {narrow ? (
          <View
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              borderWidth: 1,
              borderColor: colors.goldDim,
              backgroundColor: colors.surface,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="store" size={17} color={colors.gold} />
          </View>
        ) : null}
        <View style={{ gap: 2, flexShrink: 1 }}>
          <T size={narrow ? 18 : 21} weight={600} serif numberOfLines={1}>
            {t(titleKey as StringKey)}
          </T>
          <T size={11.5} color={colors.textFaint} numberOfLines={1}>
            {t(tagKey as StringKey)}
          </T>
        </View>
        {narrow ? (
          <View style={{ marginLeft: 'auto', flexDirection: 'row', gap: 6 }}>
            <SyncBadge compact />
            <SettingsButton compact />
          </View>
        ) : null}
      </View>

      {showSearch ? (
        <SearchBar style={narrow ? undefined : { minWidth: 220, maxWidth: 300 }} />
      ) : null}
    </View>
  );
}

function Screens({
  contentPadding,
  width,
  moreEntries,
}: {
  contentPadding: number;
  width: number;
  moreEntries: MoreEntry[];
}) {
  const view = useAppStore((s) => s.view);

  switch (view) {
    case 'more':
      return <MoreScreen contentPadding={contentPadding} entries={moreEntries} />;
    case 'menu':
      return <MenuScreen contentPadding={contentPadding} />;
    case 'order':
      return <OrderScreen contentPadding={contentPadding} width={width} />;
    case 'orders':
      return <OrdersScreen contentPadding={contentPadding} />;
    case 'customers':
      return <CustomersScreen contentPadding={contentPadding} />;
    case 'offers':
      return <OffersScreen contentPadding={contentPadding} />;
    case 'analytics':
      return <AnalyticsScreen contentPadding={contentPadding} />;
    case 'expenses':
      return <ExpensesScreen contentPadding={contentPadding} />;
    case 'admin':
      return <AdminScreen contentPadding={contentPadding} mode="admin" />;
    case 'staff':
      return <AdminScreen contentPadding={contentPadding} mode="staff" />;
    case 'dashboard':
    default:
      return <DashboardScreen contentPadding={contentPadding} />;
  }
}

function Shell() {
  const colors = useColors();
  const mode = useThemeMode();
  const { width } = useWindowDimensions();
  const userEmail = useAppStore((s) => s.userEmail);
  const refreshSession = useAppStore((s) => s.refreshSession);
  const syncNow = useAppStore((s) => s.syncNow);
  const pending = useAppStore((s) => s.sync.pending);
  const syncState = useAppStore((s) => s.sync.state);
  const signOut = useAppStore((s) => s.signOut);
  const isPlatformAdmin = useAppStore((s) => s.isPlatformAdmin);
  const role = useAppStore((s) => s.role);
  const view = useAppStore((s) => s.view);
  const setView = useAppStore((s) => s.setView);
  const t = useT();
  const [skippedSignIn, setSkippedSignIn] = useState(false);
  const insets = useInsets();
  const narrow = width < MOBILE_BREAKPOINT;
  const contentPadding = narrow ? 16 : 26;
  const navItems = useNavItems();
  const { more } = usePhoneNav(navItems);

  // 'more' exists only on a phone. Rotating a tablet to landscape, or resizing
  // a desktop window back over the breakpoint, would otherwise strand the user
  // on a screen the sidebar has no entry for.
  useEffect(() => {
    if (!narrow && view === 'more') setView('dashboard');
  }, [narrow, view, setView]);

  // An administrator has no till tabs, so the remembered view — 'dashboard'
  // for anyone who used this device before — would leave them staring at a
  // screen with no navigation back out of it.
  useEffect(() => {
    if (isPlatformAdmin && view !== 'admin') setView('admin');
  }, [isPlatformAdmin, view, setView]);

  // The owner-only tabs are chosen once per render from the current role, but
  // the *view* is remembered. Somebody demoted to worker while the app is open
  // — or signing out on a shared tablet — would otherwise be left sitting on
  // Expenses with its tab gone from the nav and no way back. The screen would
  // be empty by then (the store drops the rows on the same sync), but empty is
  // not the same as gone.
  useEffect(() => {
    if (role !== 'owner' && (view === 'expenses' || view === 'staff')) {
      setView('dashboard');
    }
  }, [role, view, setView]);

  // Restore the session on launch, then retry the outbox on a timer and
  // whenever the network comes back.
  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    const id = setInterval(() => void syncNow(), 30_000);
    const stop = onNetworkChange((online) => {
      if (online) void syncNow();
    });
    return () => {
      clearInterval(id);
      stop();
    };
  }, [syncNow]);

  // The browser paints its own background behind the app; keep it in step so
  // overscroll and the address bar do not flash the other theme.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    document.body.style.backgroundColor = colors.bg;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', colors.bg);
  }, [colors.bg]);

  // Signed in, but not on any restaurant's staff. Showing the till would be a
  // lie — every write would be refused by RLS — so say what to do instead.
  if (isCloudConfigured() && userEmail && syncState === 'no_access') {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: colors.bg,
          paddingTop: insets.top,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 28,
          gap: 12,
        }}
      >
        <StatusBar barStyle={mode === 'dark' ? 'light-content' : 'dark-content'} />
        <Icon name="lock" size={28} color={colors.gold} />
        <T size={17} weight={700} serif style={{ textAlign: 'center' }}>
          {t('no_access')}
        </T>
        <T size={13} color={colors.textFaint} style={{ textAlign: 'center' }}>
          {t('no_access_hint', { email: userEmail })}
        </T>
        <Pressable onPress={() => void signOut()} style={{ paddingVertical: 10 }}>
          <T size={13} weight={700} color={colors.gold}>
            {t('sign_out')}
          </T>
        </Pressable>
      </View>
    );
  }

  // A backend is configured but nobody has signed in: offer the login, and let
  // them skip it and keep taking orders on this device alone.
  if (isCloudConfigured() && !userEmail && !skippedSignIn) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.bg, paddingTop: insets.top }}>
        <StatusBar barStyle={mode === 'dark' ? 'light-content' : 'dark-content'} />
        <SignInScreen onSkip={() => setSkippedSignIn(true)} />
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: colors.bg, paddingTop: insets.top }}>
      <View style={{ flex: 1, flexDirection: 'row', minHeight: 0 }}>
        {narrow ? null : <Sidebar />}
        <View style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          <TopBar narrow={narrow} />
          <View style={{ flex: 1, minHeight: 0 }}>
            <Screens
              contentPadding={contentPadding}
              width={narrow ? width : width - 234}
              moreEntries={more}
            />
          </View>
        </View>
      </View>
      <StatusBar barStyle={mode === 'dark' ? 'light-content' : 'dark-content'} />
      {narrow ? <BottomNav bottomInset={insets.bottom} /> : null}
      <Toast bottomOffset={narrow ? 84 + insets.bottom : 24} />
      <OrderDetailModal />
    </View>
  );
}

/**
 * The whole product. `apps/web` and `apps/mobile` each render this and nothing
 * else — every screen, style and behaviour below here is shared.
 */
export function RestaurantApp({ fontsReady = true }: { fontsReady?: boolean }) {
  return (
    <FontsProvider ready={fontsReady}>
      <PromptProvider>

        <Shell />
      </PromptProvider>
    </FontsProvider>
  );
}
